import { request, apiBase } from '@common/request';
import gaSC from '@common/utils/ga';
import { ShowAlert } from '@common/utils/alert';

/**
 * Subscribe box.
 *
 * POST /subscribe with { email, geoId, geoCode } — the payload the Next.js
 * `subscribeEmail` sends (lib/api/subscribe.ts). geoId/geoCode come from the
 * page's goGlobal island, so a country switch needs no code change.
 *
 * Verified against the stage gateway: 201 with body `{"success":true}`. Not
 * probed on prod, which would have written a real subscriber row and mailed a
 * real welcome. So `success === false` is treated as the failure signal and any
 * other 2xx body as a pass, rather than requiring a field prod might name
 * differently.
 *
 * The one real difference from .in is the transport: JSON to the gateway
 * rather than a FormData blob to `misc/subscribe`, because that route does not
 * exist here. The outcome is reported the same way .in reports it — ShowAlert()
 * puts a `.galert` toast in the corner, which base.scss already styles.
 */

export function emailSubscriptionInit() {
  const form = document.getElementById('go-sub-form');
  if (!form || !apiBase()) return;

  const input = form.querySelector('#user-email');
  const button = form.querySelector('.gsub-button');
  let submitting = false;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    // A double-click would otherwise send the address twice. .in guards the
    // same way; the controls are also disabled below, but only once the
    // request has actually been dispatched.
    if (submitting) return;

    const email = ((input && input.value) || '').trim();
    if (!email) return ShowAlert('Email is required', { type: 'error' });
    // Same expression the Next.js component validates with, so a value one
    // accepts is never rejected by the other.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return ShowAlert('Please enter a valid email', { type: 'error' });
    }

    submitting = true;
    pending(true);
    gaSC('Email Submit Box');

    const config = window.goGlobal || {};
    request
      .post(`${apiBase()}/subscribe`, {
        email,
        geoId: config.GEO_ID,
        geoCode: config.GEO_CODE,
      })
      .then((data) => {
        if (data && data.success === false) {
          return ShowAlert(data.message || 'Failed to subscribe. Please try again.', {
            type: 'error',
          });
        }
        if (input) input.value = '';
        // .in's wording is "Best Offers Will Now Be In Your Inbox"; this is the
        // Next.js copy, which is what an International visitor already sees.
        ShowAlert('Thanks for subscribing. Coupon alerts are on their way.');
      })
      .catch(() => ShowAlert('An error occurred. Please try again later.', { type: 'error' }))
      .then(() => {
        submitting = false;
        pending(false);
      });
  });

  /**
   * In-flight state. It lives on the form as `data-state` so subscribe.scss can
   * style the wait if you want it to; the disabled controls work regardless.
   */
  function pending(active) {
    form.setAttribute('data-state', active ? 'pending' : 'idle');
    if (button) button.disabled = active;
    if (input) input.disabled = active;
  }
}
